import { AsyncPipe } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatTableDataSource } from '@angular/material/table';
import { Router, RouterLink } from '@angular/router';
import { RootState } from '@next/core-lib/app';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { SettingsActions, SettingsStorage } from '@next/core-lib/settings';
import { TableColumnTypes, TableConfig, TableComponent } from '@next/core-lib/table';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { Invoice, InvoiceColumnTypes } from '../../../invoice-data/invoice-data.model';
import {
  clearRecentInvoicesRequest,
  loadRecentInvoicesRequest,
} from '../../../invoice-data/store/invoice-data.actions';
import { selectTopRecentInvoices } from '../../../invoice-data/store/invoice-data.selectors';

import { settingsContextName, SettingsKeys } from '../../../shared/models/shared.models';

@Component({
  selector: 'app-recent-invoices',
  templateUrl: './recent-invoices.component.html',
  styleUrls: ['./recent-invoices.component.scss'],
  imports: [MatCard, RouterLink, MatCardContent, TableComponent, AsyncPipe, TranslatePipe],
})
export class RecentInvoicesComponent implements OnInit, OnDestroy {
  private readonly amountOfInvoicesToSelect = 3;

  public tableConfig$: Observable<TableConfig> = this.store
    .select(selectTopRecentInvoices(this.amountOfInvoicesToSelect))
    .pipe(
      map((invoices: Invoice[]) => ({
        dataSource: new MatTableDataSource(
          invoices?.reduce((acc, value) => [...acc, value], []) ?? [],
        ),
        columns: [
          {
            id: InvoiceColumnTypes.invoiceDate,
            type: TableColumnTypes.DATE,
            label: `dashboard.recent-invoices.${InvoiceColumnTypes.invoiceDate}`,
            headerClassName: 'flex--1-6',
            className: 'flex--1-6',
          },
          {
            id: InvoiceColumnTypes.externalInvoiceId,
            type: TableColumnTypes.STRING,
            label: `dashboard.recent-invoices.${InvoiceColumnTypes.externalInvoiceId}`,
            headerClassName: 'flex--1-4',
            className: 'flex--1-4',
          },
          {
            id: InvoiceColumnTypes.allShipToIds,
            type: TableColumnTypes.IDENTIFIER,
            label: `dashboard.recent-invoices.${InvoiceColumnTypes.allShipToIds}`,
            hideIdentifierDescription: true,
            onlyUniqueIdentifiers: true,
            headerClassName: 'flex--1-4',
            className: 'flex--1-4',
          },
          {
            id: InvoiceColumnTypes.brutoAmount,
            type: TableColumnTypes.CURRENCY,
            label: `dashboard.recent-invoices.${InvoiceColumnTypes.brutoAmount}`,
            currencyKeyColumn: InvoiceColumnTypes.currencyKey,
          },
        ],
        rowClickAction: {
          id: 'view',

          callback: this.viewInvoice.bind(this),
        },
        pagination: false,
        primaryColumn: InvoiceColumnTypes.externalInvoiceId,
        primaryColumnPrefix: this.localeService.translate(
          'dashboard.recent-invoices.primaryColumnPrefix',
        ),
        selectColumn: false,
        activeSort: {
          id: InvoiceColumnTypes.invoiceDate,
          start: 'desc',
          disableClear: false,
        },
      })),
    );

  constructor(
    private readonly store: Store<RootState>,
    private readonly localeService: LocaleService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.store.dispatch(loadRecentInvoicesRequest());
  }

  ngOnDestroy(): void {
    this.store.dispatch(clearRecentInvoicesRequest());
  }

  public setFilter(searchValue = ''): void {
    this.store.dispatch(
      SettingsActions.setSettings({
        settings: {
          contextName: settingsContextName,
          storage: SettingsStorage.PlatformSession,
          key: SettingsKeys.search,
          data: searchValue,
        },
      }),
    );
  }

  private viewInvoice(invoice: Invoice) {
    this.setFilter(invoice.externalInvoiceId);
    this.router.navigate(['invoice']);
  }
}
